import { createContext, StrictMode, Suspense, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import { ScopedCssBaseline } from '@mui/material';
import { Shell } from '@/core/containers/shell';
import { getTheme, cgpvTheme } from '@/ui/style/theme';
import { MapViewer } from '@/geo/map/map-viewer';
import { TypeMapFeaturesConfig } from '@/core/types/global-types';
import { logger } from '@/core/utils/logger';
import i18n from '@/core/translation/i18n';
import { api } from '@/app';
import { useAppDisplayThemeById } from '@/core/stores/store-interface-and-intial-values/app-state';

// create a state that will hold map config information
// TODO: use store, only keep map id on context for store manager to gather right store on hooks
export const MapContext = createContext<TypeMapContext>({
  mapId: '',
  mapFeaturesConfig: undefined,
});

/**
 * Type used for the map context
 */
export type TypeMapContext = {
  mapId: string;
  mapFeaturesConfig?: TypeMapFeaturesConfig;
};

/**
 * interface used when passing map features configuration
 */
interface AppStartProps {
  mapFeaturesConfig: TypeMapFeaturesConfig;
  onMapViewerInit?: (mapViewer: MapViewer) => void;
}

/**
 * Initialize the app with maps from inline html configs, url params
 */
function AppStart(props: AppStartProps): JSX.Element {
  // Log
  logger.logTraceRender('components/app-start');

  const { mapFeaturesConfig, onMapViewerInit } = props;
  const { mapId } = mapFeaturesConfig;

  const mapContextValue = useMemo(() => {
    // Log
    logger.logTraceUseMemo('APP-START - mapContextValue', mapId);

    return { mapId };
  }, [mapId]);

  // GV get store values by id because context is not set.... it is the only atomic selector by id
  // once context is define, map id is available
  const theme = useAppDisplayThemeById(mapId);

  /**
   * Create maps from inline configs with class name geoview-map
   * returns {JSX.Element}
   */
  function getInlineMaps(): JSX.Element {
    // create a new map viewer instance and add it to the api
    // TODO: use store, remove the use of feature by viewer class and use state to gather values
    if (!api.hasMapViewer(mapId)) {
      const mapViewer = new MapViewer(mapFeaturesConfig, i18n);
      api.setMapViewer(mapId, mapViewer, onMapViewerInit);
    }

    return (
      <I18nextProvider i18n={i18n}>
        <MapContext.Provider value={mapContextValue}>
          <ThemeProvider theme={getTheme(theme)}>
            <StrictMode>
              <Shell mapViewer={api.getMapViewer(mapId)} />
            </StrictMode>
          </ThemeProvider>
        </MapContext.Provider>
      </I18nextProvider>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={cgpvTheme}>
        <ScopedCssBaseline>
          <Suspense fallback="">{getInlineMaps()}</Suspense>
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default AppStart;
